import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import { type Renter } from '@/utils/types/renter.interface';
import { ScreeningResult } from '../constants';

const applyNewFormDefaults = (payload: Renter) => {
  // When the landlord's default coverage already includes baseline SDR
  // (sdr_months > 0), skip the customize override and submit as the
  // default flow. Otherwise the (sdr_months + 1) * rent calculation below
  // would double-count against their existing baseline.
  const sdrMonths = payload.selectedSmbPricing?.sdr_months;
  const skipCustomizeOverride =
    payload.includeDepositCoverage && sdrMonths !== undefined && sdrMonths > 0;

  if (payload.includeDepositCoverage && !skipCustomizeOverride) {
    // Express the selected DDS pricing as explicit LG/SDR coverage overrides.
    payload.rentCoverage = RentCoverageTypes.Customize;
    payload.securityDepositCoverage = SecurityDepositTypes.CustomizeOrRemove;
    payload.customCoverage = true;

    if (payload.selectedSmbPricing) {
      payload.leaseGuaranteeMonthsOverride = Math.max(0, payload.selectedSmbPricing.lg_months - 1);
      payload.securityDepositReplacementAmountOverride =
        (payload.selectedSmbPricing.sdr_months + 1) * (payload.grossMonthlyRent ?? 0);
      return;
    }

    if (payload.leaseGuaranteeMonthsOverride) {
      payload.leaseGuaranteeMonthsOverride = Math.max(
        0,
        Number(payload.leaseGuaranteeMonthsOverride) - 1,
      );
    }
    payload.securityDepositReplacementAmountOverride = payload.grossMonthlyRent ?? 0;
    return;
  }

  // Screening status identifies the selected DDS-backed default coverage, so
  // explicit override amounts must not leak from hidden form fields.
  payload.rentCoverage = RentCoverageTypes.CompanyDefault;
  payload.securityDepositCoverage = SecurityDepositTypes.CompanyDefault;
  payload.customCoverage = false;
  delete payload.leaseGuaranteeMonthsOverride;
  delete payload.securityDepositReplacementAmountOverride;
};

const applyStandardFormDefaults = (payload: Renter) => {
  if (payload.rentCoverage === RentCoverageTypes.CompanyDefault) {
    delete payload.leaseGuaranteeMonthsOverride;
  }
  if (payload.securityDepositCoverage === SecurityDepositTypes.CompanyDefault) {
    delete payload.securityDepositReplacementAmountOverride;
  }
};

export const normalizeInviteLeaseDetailsValues = (
  values: Renter,
  useNewForm: boolean,
  isSmbInvite: boolean,
): Renter => {
  const payload = { ...values };
  if (useNewForm) {
    applyNewFormDefaults(payload);
  } else {
    applyStandardFormDefaults(payload);
  }
  if (payload.customCoverage || (isSmbInvite && payload.includeDepositCoverage)) {
    payload.screeningStatus = ScreeningResult.Denied;
  }
  return payload;
};
